import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import BoardCard from './board-card';
import BoardColumn from './board-column';
import BoardFilterChips from './board-filter-chips';
import BoardToolbar from './board-toolbar';
import ViewSwitch from './view-switch';
import Carousel from '../../../common/components/carousel/carousel';
import IconButton from '../../../common/components/icon-button/icon-button';
import ErrorState from '../../../common/components/state-message/error-state';
import AlertIcon from '../../../common/icons/alert';
import { ROUTES } from '../../../core/constants/routes';
import { nameOf, useProjectCatalog, useRepositoryCatalog, useTierCatalog } from '../../../global/contexts/catalogs/catalogs';
import { useBoardTasks } from '../contexts/board';
import {
    BOARD_COLUMNS,
    BOARD_PARAMS,
    columnOrder,
    phaseIndex,
    readBoardFilters,
    sortById,
    splitFindings,
    visibleCount,
} from '../utils/board';

import '../styles/board.css';

export default function TasksBoard() {
    const [params, setParams] = useSearchParams();
    const filters = readBoardFilters(params);
    const activeIndex = phaseIndex(params);
    const { data: projects } = useProjectCatalog();
    const { data: repositories } = useRepositoryCatalog();
    const { data: tiers } = useTierCatalog();
    const { byState, isLoading, error, refetch } = useBoardTasks(filters);
    const [isBlockedOpen, setIsBlockedOpen] = useState(false);

    const update = (mutate, { replace = true } = {}) => {
        const next = new URLSearchParams(params);
        mutate(next);
        setParams(next, { replace });
    };

    const setFilter = (key, value) => update((next) => {
        if (value) next.set(key, String(value));
        else next.delete(key);
        // A repository and a tier belong to one project: keeping them would empty the board.
        if (key === BOARD_PARAMS.PROJECT) {
            next.delete(BOARD_PARAMS.REPOSITORY);
            next.delete(BOARD_PARAMS.TIER);
        }
    });

    const clearFilters = () => update((next) => {
        for (const key of [BOARD_PARAMS.REPOSITORY, BOARD_PARAMS.TIER, BOARD_PARAMS.FINDINGS]) next.delete(key);
    });

    const shownIn = (state) => {
        const { hidden } = splitFindings(byState[state].items, filters.showFindings);
        return visibleCount({ total: byState[state].total, loaded: byState[state].items.length, hidden });
    };

    const slides = BOARD_COLUMNS.map((column) => {
        const states = column.key === 'pending' ? filters.pending.states : column.states;
        const order = columnOrder(params, column);
        const all = sortById(states.flatMap((state) => byState[state].items), order);
        const { visible, hidden } = splitFindings(all, filters.showFindings);
        const counts = states.map(shownIn);
        const shown = counts.reduce((sum, item) => sum + item.count, 0);
        return {
            key: column.key,
            title: column.title,
            count: isLoading ? null : shown,
            content: (
                <BoardColumn
                    column={column}
                    tasks={visible}
                    shown={isLoading ? '—' : shown}
                    isExact={counts.every((item) => item.exact)}
                    total={states.reduce((sum, state) => sum + byState[state].total, 0)}
                    loaded={all.length}
                    hidden={hidden}
                    order={order}
                    pending={filters.pending}
                    pendingCounts={{ proposed: shownIn('proposed').count, ready: shownIn('ready').count }}
                    isLoading={isLoading}
                    tiers={tiers}
                    repositories={repositories}
                    onOrderChange={(value) => setFilter(`orden_${column.key}`, value)}
                    onPendingChange={(value) => setFilter(BOARD_PARAMS.PENDING, value)}
                    onShowFindings={() => setFilter(BOARD_PARAMS.FINDINGS, 'mostrar')}
                />
            ),
        };
    });

    const blocked = splitFindings(sortById(byState.blocked.items, columnOrder(params, { key: 'blocked', defaultOrder: 'asc' })), filters.showFindings);
    const asideTotal = byState.returned.total + byState.discarded.total;

    return (
        <div className="tasks-board">
            <header className="tasks-board__header">
                <h1 className="tasks-board__title">Tablero</h1>
                <div className="tasks-board__tools">
                    <BoardToolbar
                        filters={filters}
                        projects={projects}
                        repositories={repositories}
                        tiers={tiers}
                        onChange={setFilter}
                        onClear={clearFilters}
                    >
                        {blocked.visible.length > 0 && (
                            <IconButton
                                icon={<AlertIcon size={20} />}
                                label={`${shownIn('blocked').count} tareas bloqueadas. ${isBlockedOpen ? 'Ocultar' : 'Mostrar'}`}
                                tone="danger"
                                badge={shownIn('blocked').count}
                                isActive={isBlockedOpen}
                                aria-expanded={isBlockedOpen}
                                aria-controls="tasks-board-blocked"
                                onClick={() => setIsBlockedOpen((open) => !open)}
                            />
                        )}
                    </BoardToolbar>
                    <ViewSwitch current="tablero" />
                </div>
            </header>
            <p className="tasks-board__description">
                Cuatro fases del trabajo. En el móvil, desliza de derecha a izquierda para pasar a la siguiente.
            </p>
            <BoardFilterChips
                filters={filters}
                projects={projects}
                repositories={repositories}
                tiers={tiers}
                onChange={setFilter}
            />

            {error && <ErrorState error={error} title="No se pudieron cargar las tareas" onRetry={refetch} />}

            {!error && isBlockedOpen && blocked.visible.length > 0 && (
                <section id="tasks-board-blocked" className="tasks-board__blocked" aria-label="Tareas bloqueadas">
                    <h2 className="tasks-board__blocked-title">Bloqueadas</h2>
                    <ul className="tasks-board__blocked-cards">
                        {blocked.visible.map((task) => (
                            <BoardCard
                                key={task.id}
                                task={task}
                                tierName={task.tierId ? nameOf(tiers, task.tierId, 'Tier') : null}
                                repository={repositories?.get(task.repositoryId)}
                            />
                        ))}
                    </ul>
                </section>
            )}

            {!error && (
                <Carousel
                    label="Tablero de trabajo"
                    slides={slides}
                    activeIndex={activeIndex}
                    onActiveChange={(index) => setFilter(BOARD_PARAMS.PHASE, index === 0 ? '' : BOARD_COLUMNS[index].key)}
                />
            )}

            <footer className="tasks-board__foot">
                <p>
                    El tablero se mira, no se arrastra: una tarea cambia de fase al tomarla, entregarla o revisarla.{' '}
                    <Link to={ROUTES.REVIEWS} className="tasks-board__link">Lo que está en revisión se decide aquí</Link>.
                </p>
                {asideTotal > 0 && (
                    <p>
                        Hay <strong>{asideTotal}</strong> tareas devueltas o descartadas fuera de las fases.{' '}
                        <Link to={`${ROUTES.WORK}?vista=lista&state=returned`} className="tasks-board__link">Verlas en la lista</Link>
                    </p>
                )}
            </footer>
        </div>
    );
}
